import { TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useContext, useState } from "react";
import toast from "react-hot-toast";
import { withServerReason } from "../../../utils/apiErrorMessage";
import AffectedReservationsModal from "../../../components/lines-managements/trips/AffectedReservationsModal";
import Button from "../../../components/ui/Button";
import IconBtn from "../../../components/ui/IconBtn";
import Modal from "../../../components/ui/Modal";
import useSuperAdmin from "../../../components/users/permissions/useSuperAdmin";
import { LinesContext } from "../../../context/LinesProvider";
import { IJourneyReservationPassengerInfo } from "../../../interfaces/endpoints/trips/TripChangeNotification";
import {
  deleteLine,
  getLineDeletePreview,
  notifyLineDeleteEmails
} from "../../../services/Lines.service";

interface DeleteLineProps {
  index: number;
}
const DeleteLine: FC<DeleteLineProps> = ({ index }) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showAffectedReservationsModal, setShowAffectedReservationsModal] = useState(false);
  const [affectedReservations, setAffectedReservations] = useState<IJourneyReservationPassengerInfo[]>([]);
  const { deleteLine: deleteLineFromContext } = useContext(LinesContext);
  const isSuperAdmin = useSuperAdmin();
  // eslint-disable-next-line @typescript-eslint/space-before-function-paren
  const handleDelete = async (id: number) => {
    try {
      // First delete the line
      await deleteLine(id);
      setDeleteModal(false);
      toast.success("Linja është fshirë me sukses.", { position: "top-right" });

      // For non-super admins, skip preview/notifications
      if (!isSuperAdmin) {
        deleteLineFromContext(id);
        return;
      }

      // Then check for affected reservations
      const preview = await getLineDeletePreview(id);
      const flattened = (preview.data || [])
        .flatMap((p) => p.affectedPassengers || []);
      if (flattened.length > 0) {
        setAffectedReservations(flattened);
        setShowAffectedReservationsModal(true);
      } else{
        deleteLineFromContext(id);
      }
    } catch (error) {
      toast.error(withServerReason("Ndodhi një gabim gjatë fshirjes së linjës.", error), { duration: 6000 });
    }
  };

  const handleNotifyConfirm = async (selectedReservationIds: string[]) => {
    try {
      await notifyLineDeleteEmails(index, selectedReservationIds);
      deleteLineFromContext(index);
      toast.success("Emailat e njoftimit u dërguan me sukses.", { position: "top-right" });
    } catch {
      toast.error("Ndodhi një gabim gjatë dërgimit të emailave.", { position: "top-right" });
    }
    setShowAffectedReservationsModal(false);
  };

  const handleContinueWithoutNotifying = () => {
    setShowAffectedReservationsModal(false);
    deleteLineFromContext(index);
  };

  return (
    <>
      <IconBtn className="md:p-1" onClick={() => setDeleteModal(true)}>
        <TrashIcon className="w-5 h-5 md:w-4 md:h-4" />
      </IconBtn>
      <Modal
        hasHeader={false}
        position="center"
        show={deleteModal}
        onClose={() => setDeleteModal(false)}
        mini
        calcHeight={false}
        buttonElement={
          <Button onClick={() => handleDelete(index)}>Konfirmo</Button>
        }
      >
        <div className="flex justify-center mt-6">
          Konfirmo fshirjen e linjës.
          <IconBtn
            className="absolute right-2 top-2"
            onClick={() => setDeleteModal(false)}
          >
            <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
          </IconBtn>
        </div>
        <p className="justify-center w-full flex mt-6 text-gray-600 text-sm">
          Ky veprim nuk mund të kthehet mbrapa.
        </p>
      </Modal>
      {isSuperAdmin && (
        <AffectedReservationsModal
          isOpen={showAffectedReservationsModal}
          onClose={handleContinueWithoutNotifying}
          onConfirm={handleNotifyConfirm}
          affectedReservations={affectedReservations}
          tripId={0}
          contextText="Fshirja e linjës do të ndikojë në rezervimet e mëposhtme. Zgjidhni cilat të njoftohen:"
        />
      )}
    </>
  );
};

export default DeleteLine;
